import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring, useMotionValue, useTransform } from "framer-motion";
import {
  Cloud, Braces, Database, ShieldCheck, BarChart3, Workflow, ArrowRight,
  ChevronLeft, ChevronRight, PhoneCall, Video, House, Cpu,
} from "lucide-react";
import DashboardPreview from "./DashboardPreview";
import { scrollToId } from "../../lib/lenis";
import { EASE } from "../../lib/motion";

const SLIDES = [
  {
    id: "coopsys",
    eyebrow: "Software · Cloud · FinTech",
    lines: [[{ t: "Tecnología que" }], [{ t: "transforma ", g: true }, { t: "la forma" }], [{ t: "de hacer negocios." }]],
    desc: "Diseñamos software, plataformas cloud y soluciones digitales que ayudan a empresas e instituciones financieras a operar de forma más inteligente, segura y eficiente.",
    cta: { label: "Conocer CoopSys", to: "/coopsys" },
    visual: "dashboard",
  },
  {
    id: "empresarial",
    eyebrow: "Software Empresarial",
    lines: [[{ t: "Software diseñado" }], [{ t: "alrededor ", g: true }, { t: "de tu negocio." }]],
    desc: "Sistemas para administrar y automatizar operaciones empresariales: procesos más ágiles y decisiones mejor informadas.",
    cta: { label: "Ver soluciones", to: "/servicios" },
    img: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000&auto=format&fit=crop",
    icon: Cpu,
  },
  {
    id: "cloud",
    eyebrow: "Cloud & Infraestructura",
    lines: [[{ t: "Tu operación." }], [{ t: "En la nube.", g: true }]],
    desc: "Accede a la información de tu organización desde cualquier lugar y reduce la dependencia de infraestructura local.",
    cta: { label: "Explorar Cloud", to: "/servicios" },
    img: "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000&auto=format&fit=crop",
    icon: Cloud,
  },
  {
    id: "cctv",
    eyebrow: "Seguridad y CCTV",
    lines: [[{ t: "Protege lo que tu" }], [{ t: "operación ha construido.", g: true }]],
    desc: "Instalación de sistemas de seguridad y videovigilancia, con monitoreo y buenas prácticas en cada capa.",
    cta: { label: "Solicitar instalación", to: "/contacto" },
    img: "https://images.unsplash.com/photo-1618482914248-29272d021005?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000&auto=format&fit=crop",
    icon: Video,
  },
  {
    id: "smart-home",
    eyebrow: "Smart Home — Domótica",
    lines: [[{ t: "Espacios inteligentes," }], [{ t: "vida conectada.", g: true }]],
    desc: "Automatización de iluminación, clima y seguridad para hogares y oficinas que quieren avanzar.",
    cta: { label: "Solicitar información", to: "/contacto" },
    img: "https://images.unsplash.com/photo-1650091507687-5ea34d80e674?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000&auto=format&fit=crop",
    icon: House,
  },
];

const PARTICLES = [
  { icon: Cloud, label: "Cloud", pos: "left-[-9%] top-[8%]", delay: "0s", enter: 0.9 },
  { icon: Braces, label: "API", pos: "right-[-5%] top-[16%]", delay: "1.2s", enter: 1.05 },
  { icon: Database, label: "Database", pos: "left-[-7%] top-[42%]", delay: "0.6s", enter: 1.2 },
  { icon: ShieldCheck, label: "Security", pos: "right-[-7%] bottom-[34%]", delay: "1.8s", enter: 1.35 },
  { icon: BarChart3, label: "Analytics", pos: "left-[6%] bottom-[-7%]", delay: "0.3s", enter: 1.5 },
  { icon: Workflow, label: "Automation", pos: "right-[4%] bottom-[-9%]", delay: "1.5s", enter: 1.65 },
];

function ImageVisual({ slide }) {
  return (
    <div
      className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0A1120] shadow-[0_40px_120px_-30px_rgba(37,99,235,0.5)]"
      data-testid={`hero-visual-${slide.id}`}
    >
      <img src={slide.img} alt={slide.eyebrow} className="h-full w-full object-cover" loading="eager" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#070B12]/85 via-[#070B12]/15 to-transparent" aria-hidden="true" />
      <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white backdrop-blur">
        <slide.icon className="h-5 w-5" strokeWidth={1.8} />
      </span>
      <div className="absolute bottom-6 left-6 right-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-sky-300">{slide.eyebrow}</p>
        <p className="mt-2 font-display text-xl font-bold text-white sm:text-2xl">
          {slide.lines.map((line) => line.map((seg) => seg.t).join(""))}
        </p>
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(my, { stiffness: 55, damping: 15 });
  const rotateY = useSpring(mx, { stiffness: 55, damping: 15 });
  const { scrollY } = useScroll();
  const dashY = useTransform(scrollY, [0, 700], [0, -46]);

  const slide = SLIDES[idx];
  const N = SLIDES.length;

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % N), 6500);
    return () => clearInterval(id);
  }, [paused, idx, N]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    mx.set(px * 7);
    my.set(-py * 6);
  };

  const arrowCls =
    "flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card/70 text-muted-foreground transition-all duration-300 hover:border-electric/50 hover:text-electric";

  return (
    <section
      className="relative overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-24"
      id="inicio"
      data-testid="hero-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 grid-bg [mask-image:radial-gradient(ellipse_75%_65%_at_50%_0%,black,transparent)]" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[720px] rounded-full bg-brand/15 blur-[140px]" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-[-30%] left-[-12%] h-[480px] w-[560px] rounded-full bg-electric/10 blur-[130px]" aria-hidden="true" />

      <div className="wrap relative grid items-center gap-14 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-6 xl:col-span-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <motion.span
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-4 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {slide.eyebrow}
              </motion.span>

              <h1
                className="mt-7 text-4xl font-extrabold leading-[1.06] sm:text-5xl lg:text-[3.2rem] xl:text-[3.6rem]"
                data-testid={`hero-title-${slide.id}`}
              >
                {slide.lines.map((line, i) => (
                  <span key={`l-${i}`} className="mask-line">
                    <motion.span
                      className="block"
                      initial={{ y: "112%" }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.9, ease: EASE, delay: 0.08 + i * 0.11 }}
                    >
                      {line.map((seg, j) =>
                        seg.g ? (
                          <span key={j} className="text-gradient">{seg.t}</span>
                        ) : (
                          <span key={j}>{seg.t}</span>
                        ),
                      )}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.4 }}
                className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
              >
                {slide.desc}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.52 }}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <Link
                  to={slide.cta.to}
                  data-testid={`hero-primary-cta-${slide.id}`}
                  className="group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-brand-foreground shadow-glow-sm transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-strong"
                >
                  {slide.cta.label}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
                <button
                  onClick={() => scrollToId("contacto")}
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-7 py-3.5 text-sm font-semibold text-foreground transition-all duration-300 hover:-translate-y-0.5 hover:border-electric/50 hover:text-electric"
                >
                  Hablar con un especialista
                </button>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex items-center gap-4"
            data-testid="hero-slide-controls"
          >
            <button onClick={() => setIdx((idx - 1 + N) % N)} aria-label="Producto anterior" data-testid="hero-slide-prev-button" className={arrowCls}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2">
              {SLIDES.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setIdx(i)}
                  aria-label={`Ver ${s.eyebrow}`}
                  data-testid={`hero-slide-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === idx ? "w-8 bg-electric" : "w-3 bg-border hover:bg-muted-foreground/50"
                  }`}
                />
              ))}
            </div>
            <button onClick={() => setIdx((idx + 1) % N)} aria-label="Producto siguiente" data-testid="hero-slide-next-button" className={arrowCls}>
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="num ml-1 hidden font-mono text-[11px] text-muted-foreground sm:inline" data-testid="hero-slide-counter">
              {String(idx + 1).padStart(2, "0")} / {String(N).padStart(2, "0")}
            </span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-6" ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          {PARTICLES.map((p) => (
            <motion.span
              key={p.label}
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: EASE, delay: p.enter }}
              className={`absolute z-10 hidden items-center gap-2 rounded-full border border-border bg-card/85 px-3.5 py-2 text-xs font-medium text-foreground shadow-card backdrop-blur md:flex ${p.pos}`}
            >
              <span className="animate-float" style={{ animationDelay: p.delay }}>
                <p.icon className="h-3.5 w-3.5 text-electric" />
              </span>
              {p.label}
            </motion.span>
          ))}
          <motion.div style={{ y: dashY, rotateX, rotateY, transformPerspective: 1200 }} className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, scale: 0.97, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: -12 }}
                transition={{ duration: 0.55, ease: EASE }}
              >
                {slide.visual === "dashboard" ? (
                  <DashboardPreview />
                ) : (
                  <ImageVisual slide={slide} />
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
